import { useMemo } from "react";
import { useSync } from "@tldraw/sync";
import { Tldraw, Editor, TLAssetStore, TLComponents } from "tldraw";
import "tldraw/tldraw.css";

// Simple asset store that keeps images inline as base64
// For production, you'd want to upload to server and return URLs
const assetStore: TLAssetStore = {
  async upload(_asset, file) {
    // Convert file to base64 data URL and return { src }
    const src = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

    return { src };
  },
  resolve(asset) {
    return asset.props.src;
  },
};

interface SyncedEditorProps {
  noteId: number;
  isStyleOpen: boolean;
  onEditorMount?: (editor: Editor) => void;
}

export default function SyncedEditor({ noteId, isStyleOpen, onEditorMount }: SyncedEditorProps) {
  // Build WebSocket URL - use same host as the page, just switch protocol
  const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  const wsUri = `${wsProtocol}//${window.location.host}/sync/${noteId}`;

  const store = useSync({
    uri: wsUri,
    assets: assetStore,
  });

  // Toolbar stays hidden (surfaced in CanvasToolbar). StylePanel key must be
  // omitted (not set to undefined) when open — tldraw spreads overrides over
  // its defaults, so `{ StylePanel: undefined }` would overwrite the default.
  const components = useMemo<TLComponents>(
    () => (isStyleOpen ? { Toolbar: null } : { Toolbar: null, StylePanel: null }),
    [isStyleOpen]
  );

  if (store.status === "loading") {
    return (
      <div className="sync-loading">
        <div className="sync-spinner" />
        <span>Connecting...</span>
      </div>
    );
  }

  if (store.status === "error") {
    return (
      <div className="sync-error">
        <span>Connection error</span>
        <span className="sync-error-detail">{store.error.message}</span>
      </div>
    );
  }

  return (
    <Tldraw
      store={store}
      components={components}
      onMount={(editor) => {
        editor.setCurrentTool("draw");
        onEditorMount?.(editor);
      }}
    />
  );
}
